import { chatService } from '../../services/chat-service.js';
import { ButtonOption } from './types.js';
import { ActionLockManager } from './action-lock-manager.js';
import { autoResizeTextarea } from '../../utils/chat-utils.js';

interface UserInteractionOptions {
  senderId: string;
  getInputValue: () => string;
  setInputValue: (value: string) => void;
  hasEstablishedSession: () => boolean;
  isBufferingInitial: () => boolean;
  isConnected: () => boolean;
  getIsOffline: () => boolean;
  onOfflineInput: () => void;
  getIsResponding: () => boolean;
  addMessage: (message: string, sender: string, direction: 'incoming' | 'outgoing', buttons?: ButtonOption[]) => void;
  setIsTyping: (isTyping: boolean) => void;
  setIsResponding: (isResponding: boolean) => void;
  getSelectedValues: () => string[];
  clearSelectedValues: () => void;
  onSendError?: () => void;
  attemptReconnect?: () => Promise<boolean>;
}

// Handles user input interactions (send, buttons, typing) separately from the UI component.
export class UserInteractionHandler {
  constructor(private readonly options: UserInteractionOptions) { }

  private readonly actionLockManager = new ActionLockManager();

  handleInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    this.options.setInputValue(target.value);
    autoResizeTextarea(target);
  };

  handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Block Enter key until chat session is created and the initial greeting has been shown
      if (!this.options.hasEstablishedSession() || this.options.isBufferingInitial()) {
        e.preventDefault();
        return;
      }

      // Block Enter key if the LLM is responding
      if (this.options.getIsResponding()) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      void this.handleSend();
    }
  };

  handleButtonClick = async (e: CustomEvent<ButtonOption & { selectedValues?: string[] }>) => {
    const actionElement = this.actionLockManager.getActionElement(e);
    if (this.actionLockManager.markPending(actionElement)) {
      return;
    }

    const button = e.detail;
    if (this.options.getIsOffline() || !navigator.onLine) {
      this.actionLockManager.clearPending(actionElement);
      this.options.onOfflineInput();
      return;
    }
    this.options.setIsTyping(true);
    this.options.setIsResponding(true);

    if (!this.options.isConnected()) {
      // Scenario: User clicks button but WebSocket is not connected — attempt reconnection
      const reconnected = await this.options.attemptReconnect?.();
      if (!reconnected) {
        this.actionLockManager.clearPending(actionElement);
        this.options.setIsTyping(false);
        this.options.setIsResponding(false);
        // attemptReconnect already shows the error message in UI
        return;
      }
    }

    this.options.addMessage(button.label, this.options.senderId, 'outgoing');

    try {
      // selectedValues from the event detail (e.g. recipe-action) take priority,
      // otherwise fall back to globally tracked selections (e.g. coupon checkboxes).
      const selectedValues = button.selectedValues ?? this.options.getSelectedValues();
      const extra: { id?: string; values: string[] } = { values: selectedValues };
      if (button.value) {
        extra.id = button.value;
      }
      await chatService.sendMessage(button.label, extra);
      this.options.clearSelectedValues();
      // Pending cleared here; the actionsLocked flag is derived from the message list
      // (an outgoing message now follows this action block) so no explicit DOM lock needed.
      this.actionLockManager.clearPending(actionElement);
    } catch (error) {
      // Scenario: Failed to send button message (WebSocket failed, timeout, etc)
      this.actionLockManager.clearPending(actionElement);
      this.options.setIsTyping(false);
      this.options.setIsResponding(false);
      this.options.onSendError?.();
    }
  };

  handleSend = async () => {
    if (!this.options.hasEstablishedSession() || this.options.isBufferingInitial()) return;

    const message = this.options.getInputValue().trim();
    if (!message) return;

    // Always show the user's message in the chat and clear the input
    this.options.addMessage(message, this.options.senderId, 'outgoing');
    this.options.setInputValue('');

    if (this.options.getIsOffline() || !navigator.onLine) {
      this.options.onOfflineInput();
      return;
    }

    this.options.setIsTyping(true);
    this.options.setIsResponding(true);

    if (!this.options.isConnected()) {
      // Scenario: User attempts to send message but WebSocket is not connected — attempt reconnection
      const reconnected = await this.options.attemptReconnect?.();
      if (!reconnected) {
        this.options.setIsTyping(false);
        this.options.setIsResponding(false);
        // attemptReconnect already shows the error message in UI
        return;
      }
    }

    try {
      await chatService.sendMessage(message);
    } catch (error) {
      // Scenario: Failed to send text message (WebSocket failed, timeout, etc)
      this.options.setIsTyping(false);
      this.options.setIsResponding(false);
      this.options.onSendError?.();
    }
  };
}
